'use client';

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { User } from '@/types';
import { authApi } from '@/api/auth';
import { trackLogin } from '@/lib/analytics';
import { usersApi } from '@/api/users';
import { sessionsApi } from '@/api/sessions';
import { getDeviceInfo } from '@/utils/device-utils';
import { apiClient } from '@/api/client';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Request a 6-digit code to be sent to email */
  requestCode: (email: string) => Promise<boolean>;
  /** Verify the 6-digit code and complete login */
  verifyCode: (email: string, code: string) => Promise<boolean>;
  /** Complete login with tokens (e.g. after OAuth callback). Optionally pass refresh_token to store it. */
  loginWithToken: (accessToken: string, refreshToken?: string) => Promise<boolean>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  /** Updates user from API response (e.g. setCurrentWorkspace) to avoid extra auth/me call */
  updateUserFromApi: (user: User) => void;
  refreshCredits: () => Promise<void>;
  updateLocalUser: (patch: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const USER_STORAGE_KEY = 'auth_user';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Handle authentication errors globally
  const handleAuthError = useCallback(() => {
    console.warn('Authentication error detected. Logging out...');
    authApi.logout();
    setUser(null);
    saveUserToStorage(null);
  }, []);

  // Load user from localStorage
  const loadUserFromStorage = (): User | null => {
    if (typeof window === 'undefined') return null;
    
    try {
      const storedUser = localStorage.getItem(USER_STORAGE_KEY);
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      console.error('Error loading user from storage:', error);
      return null;
    }
  };

  // Save user to localStorage
  const saveUserToStorage = (userData: User | null) => {
    if (typeof window === 'undefined') return;
    
    try {
      if (userData) {
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
      } else {
        localStorage.removeItem(USER_STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error saving user to storage:', error);
    }
  };

  // Validate token and load user data
  const validateAndLoadUser = async () => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
    
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      // Try to get user data from API
      const userData = await authApi.me();
      setUser(userData);
      saveUserToStorage(userData);
    } catch (error) {
      console.error('Token validation failed:', error);
      // Token is invalid, clear auth data
      authApi.logout();
      setUser(null);
      saveUserToStorage(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if user is already authenticated on mount
  useEffect(() => {
    // Set up global auth error handler
    apiClient.setAuthErrorHandler(handleAuthError);

    const checkAuth = async () => {
      // First, try to load user from localStorage
      const storedUser = loadUserFromStorage();
      
      if (storedUser) {
        setUser(storedUser);
        // Validate token in the background
        validateAndLoadUser();
      } else {
        // No stored user, just validate if there's a token
        await validateAndLoadUser();
      }
    };
    
    checkAuth();
  }, [handleAuthError]);

  const requestCode = async (email: string): Promise<boolean> => {
    try {
      await authApi.requestCode({ email });
      return true;
    } catch (error) {
      console.error('Request code error:', error);
      return false;
    }
  };

  const verifyCode = async (email: string, code: string): Promise<boolean> => {
    try {
      const response = await authApi.verifyCode({ email, code });
      setUser(response.user);
      saveUserToStorage(response.user);
      trackLogin('email');
      try {
        const deviceInfo = getDeviceInfo();
        await sessionsApi.create({
          deviceId: deviceInfo.deviceId,
          deviceName: deviceInfo.deviceName,
          deviceType: deviceInfo.deviceType,
          browser: deviceInfo.browser,
          userAgent: deviceInfo.userAgent,
        });
      } catch (sessionError) {
        console.warn('Failed to create session:', sessionError);
      }
      return true;
    } catch (error) {
      console.error('Verify code error:', error);
      return false;
    }
  };

  const loginWithToken = useCallback(
    async (
      accessToken: string,
      refreshToken?: string
    ): Promise<boolean> => {
      try {
        apiClient.setToken(accessToken);
        if (refreshToken && typeof window !== 'undefined') {
          localStorage.setItem('refresh_token', refreshToken);
        }
        const userData = await authApi.me();
        setUser(userData);
        saveUserToStorage(userData);
        trackLogin('google');
        return true;
      } catch (error) {
        console.error('Login with token error:', error);
        apiClient.setToken(null);
        if (typeof window !== 'undefined') localStorage.removeItem('refresh_token');
        return false;
      }
    },
    []
  );

  const logout = () => {
    authApi.logout();
    setUser(null);
    saveUserToStorage(null);
  };

  const refreshUser = async () => {
    try {
      const userData = await authApi.me();
      setUser(userData);
      saveUserToStorage(userData);
    } catch (error) {
      console.error('Error refreshing user:', error);
      // If refresh fails, logout
      logout();
    }
  };

  const updateLocalUser = useCallback((patch: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      saveUserToStorage(next);
      return next;
    });
  }, []);

  const updateUserFromApi = useCallback((userData: User) => {
    setUser(userData);
    saveUserToStorage(userData);
  }, []);

  const refreshCredits = useCallback(async () => {
    try {
      const creditsData = await usersApi.getCredits();
      updateLocalUser({ credits: creditsData.credits });
    } catch (error) {
      console.error('Error refreshing credits:', error);
    }
  }, [updateLocalUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        requestCode,
        verifyCode,
        loginWithToken,
        logout,
        refreshUser,
        updateUserFromApi,
        refreshCredits,
        updateLocalUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
